import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { Panel, SectionLabel, Skeleton, Stat } from '@/components/ui'
import { PairGlyph } from '@/components/dashboard/TokenGlyph'
import { DetailPageShell, SubTabs } from '@/components/dashboard/detail/DetailPageShell'
import { StatGrid } from '@/components/dashboard/detail/StatGrid'
import { FaqAccordion } from '@/components/dashboard/detail/FaqAccordion'
import { CurveChart } from '@/components/dashboard/detail/CurveChart'
import { LoopForm } from '@/components/dashboard/multiply/LoopForm'
import { useLoopDetail } from '@/hooks/useLoops'
import { useLoopPosition, computeLoopPnl } from '@/hooks/useLoopPosition'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { buildLeverageCurve, loopReturnCase, currentLeverage, liquidationPrice, carryPresentation } from '@/lib/loops'
import { multiplyFaq, fmtDuration } from '@/lib/faqContent'
import { fmt, fmtPct, fmtUsd, fmtBps } from '@/lib/utils'

// ─────────────────────────────────────────────────────────────────
// Per-loop detail page.
//
// This page exists after an explicit reversal. The earlier judgement was that Multiply should stay
// a panel on the list, on the grounds that a detail page would mostly be the deposit form again.
// That was wrong, and specifically why: it is not the form that justifies the page, it is
// everything around it - the leverage curve, the venue, the position readout, the risk that is
// genuinely peculiar to a correlated pair, and the FAQ. See UI_PLAN.md.
// ─────────────────────────────────────────────────────────────────

export function MultiplyDetailSection({ id }) {
  const { loop, isLoading, notFound, notDeployed, refetch } = useLoopDetail(id)

  if (notDeployed) {
    return <NoticeCard title="Multiply is live on another network."
      body="Point your wallet at a network where Horizon's lending pool and router are deployed." />
  }
  if (notFound) {
    return <NoticeCard title="Loop not found."
      body="Horizon offers a different set of loops. Pick one from the Multiply page." />
  }
  if (isLoading || !loop) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        <Skeleton width={280} height={32} />
        <div style={cardsRow}><Skeleton width="100%" height={90} /><Skeleton width="100%" height={90} /><Skeleton width="100%" height={90} /></div>
      </div>
    )
  }

  return <LoopDetail loop={loop} refetch={refetch} />
}

function NoticeCard({ title, body }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Link to="/multiply" style={backLinkStyle}>← Multiply</Link>
      <Panel elevated style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>{title}</div>
        <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>{body}</div>
      </Panel>
    </div>
  )
}

function LoopDetail({ loop, refetch }) {
  const { address: user } = useAccount()
  const narrow = useMediaQuery('(max-width: 1100px)')
  const { collateral, debt } = loop

  const spotRatio = debt.priceUsd > 0 ? collateral.priceUsd / debt.priceUsd : 0

  return (
    <DetailPageShell
      backTo="/multiply"
      backLabel="Back to Multiply"
      breadcrumb={[{ label: 'Multiply', to: '/multiply' }, { label: loop.name }]}
      glyph={<PairGlyph symbols={[collateral.symbol, debt.symbol]} />}
      title={`${collateral.symbol}/${debt.symbol} Multiply`}
      badges={[
        { label: loop.kind, color: T.accent },
        loop.configured ? null : { label: 'Venue pending', color: T.warning },
        loop.openingPaused ? { label: 'Opening paused', color: T.warning } : null,
        loop.venueDormant && !loop.openingPaused ? { label: 'Venue warming up', color: T.warning } : null,
      ].filter(Boolean)}
      meta={[
        { label: `${collateral.symbol} price`, value: fmtUsd(collateral.priceUsd) },
        { label: `${collateral.symbol}/${debt.symbol}`, value: spotRatio > 0 ? fmt(spotRatio, 4) : '—' },
        { label: 'Max leverage', value: `${loop.leverage.toFixed(1)}x` },
      ]}
      tabs={[
        { id: 'overview', label: 'Overview' },
        { id: 'position', label: 'My Position' },
        { id: 'info', label: 'Info & FAQs' },
      ]}
    >
      {(tab) => (
        <div style={narrow ? layoutNarrow : layout}>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
            {tab === 'overview' && <OverviewTab loop={loop} />}
            {tab === 'position' && <PositionTab loop={loop} user={user} />}
            {tab === 'info' && <InfoTab loop={loop} />}
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={{ position: 'sticky', top: 20 }}>
              {loop.configured
                ? <LoopForm loop={loop} user={user} onTx={refetch} />
                : <VenuePendingNote loop={loop} />}
            </div>
          </div>
        </div>
      )}
    </DetailPageShell>
  )
}

/// Shown in place of the form when the router has no swap venue for this pair. Honest rather than
/// hopeful: the button would revert SwapPoolNotConfigured, and a control that cannot succeed is
/// worse than an explanation of what is missing.
function VenuePendingNote({ loop }) {
  return (
    <Panel style={{ padding: 28 }}>
      <SectionLabel>Opening becomes available soon</SectionLabel>
      <div style={{ marginTop: 12, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.7 }}>
        This loop trades {loop.collateral.symbol} against {loop.debt.symbol} on every open, so it needs
        a swap venue pointed at that pair. Genesis configures one on the router before the first
        position can be built. Everything else on this page is live.
      </div>
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────────

function OverviewTab({ loop }) {
  const [view, setView] = useState('loop')
  const { collateral, debt } = loop

  return (
    <>
      <div style={cardsRow}>
        <Stat boxed label="Liquidity available" value={fmtUsd(loop.liquidityUsd)} sub={`${debt.symbol} left to borrow`} />
        <Stat boxed label="Max leverage" value={`${loop.leverage.toFixed(1)}x`} sub={`set by ${collateral.symbol}'s collateral factor`} />
        <Stat boxed
          label="Net APY at max leverage"
          value={fmtBps(loop.allInNetApyBps)}
          sub={loop.allInNetApyBps !== loop.netApyBps
            ? `interest carry ${fmtBps(loop.netApyBps)} plus rewards at today's rates`
            : carryPresentation(loop, loop.netApyBps).note}
          color={TONE[carryPresentation(loop, loop.allInNetApyBps).tone]}
        />
      </div>

      <Panel style={{ padding: 28 }}>
        <div style={panelHeader}>
          <SectionLabel>Loop parameters</SectionLabel>
          <SubTabs
            tabs={[{ id: 'loop', label: 'Loop info' }, { id: 'advanced', label: 'Advanced' }]}
            active={view}
            onSelect={setView}
          />
        </div>

        <div style={{ marginTop: 18 }}>
          {view === 'loop' ? (
            <StatGrid items={[
              { label: 'Collateral asset', value: collateral.symbol },
              { label: 'Debt asset', value: debt.symbol },
              { label: 'Max LTV', value: fmtBps(collateral.collateralFactorBps), help: 'The most that may be borrowed against this collateral, which is what sets the leverage ceiling.' },
              { label: 'Liquidation threshold', value: fmtBps(collateral.liquidationThresholdBps) },
              { label: `${collateral.symbol} supply APY`, value: fmtBps(collateral.supplyRateBps), color: T.positive },
              { label: `${debt.symbol} borrow APR`, value: fmtBps(debt.borrowRateBps) },
              { label: 'Borrow rate type', value: 'Variable', help: 'The borrow rate follows utilisation in the debt reserve, so it moves while the position is open.' },
              { label: 'Rate spread', value: fmtPct((collateral.supplyRateBps - debt.borrowRateBps) / 100), help: 'What one unit of exposure earns before leverage. Leverage multiplies this figure, in both directions.' },
            ]} />
          ) : (
            <StatGrid items={[
              { label: `${collateral.symbol} supplied`, value: fmtUsd(loop.suppliedUsd), help: 'Protocol-wide, across every supplier of this reserve.' },
              { label: `${debt.symbol} borrowed`, value: fmtUsd(loop.borrowedUsd) },
              { label: 'Liquidation bonus', value: fmtBps(collateral.liquidationBonusBps) },
              { label: 'Liquidation fee', value: fmtBps(collateral.liquidationFeeBps) },
              { label: 'Swap venue', value: <AddressValue address={loop.venuePool} /> },
              { label: 'Venue fee tier', value: loop.venueFee != null ? fmtPct(loop.venueFee / 10_000) : '—' },
              { label: 'Venue configured', value: loop.venueSetAtSec ? new Date(loop.venueSetAtSec * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—', help: 'When genesis pointed this pair at its swap venue. Opening becomes available a set period after this, so a freshly-pointed pool has the observation history a TWAP floor needs.' },
              { label: 'Gas, round trip', value: '1.62M', help: 'Measured against a real seeded venue: 810k to open, 806k to close. Both legs are a single swap.' },
            ]} />
          )}
        </div>
      </Panel>

      <Panel style={{ padding: 28 }}>
        <SectionLabel>How this loop works</SectionLabel>
        <div style={{ marginTop: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.7, maxWidth: 720 }}>
          {loop.blurb}
        </div>
      </Panel>

      <LeverageCurvePanel loop={loop} />
    </>
  )
}

/// Net APY across the whole leverage range.
///
/// The reference charts historical net APY at four fixed multipliers. Sampling the live curve
/// shows the thing a fixed-multiplier history cannot: WHERE THE LINE CROSSES ZERO. Past that
/// leverage the borrow cost exceeds what the extra exposure earns, so the position loses money by
/// design rather than by misfortune - and because the crossing moves with the rates, it has to be
/// computed live rather than recorded.
function LeverageCurvePanel({ loop }) {
  const { collateral, debt } = loop
  // The loop's own effective legs (interest + HORIZON deposit-aware + intrinsic collateral yield,
  // computed once in useLoops), matching the headline Net APY - a chart telling a different story
  // under that headline would have the two disagree about whether the loop earns.
  const earnBps = loop.allInEarnBps
  const costBps = loop.allInCostBps
  const hasRewards = earnBps !== collateral.supplyRateBps || costBps !== debt.borrowRateBps
  const curve = buildLeverageCurve(earnBps, costBps, loop.leverage)
  if (curve.length === 0) return null

  const rc = loopReturnCase(earnBps, costBps, loop.leverage)
  const markers = rc.breakEven
    ? [{ x: rc.breakEven, y: 0, label: `Breaks even at ${rc.breakEven.toFixed(2)}x`, color: T.warning, dashed: true }]
    : []
  const story = {
    crossesWithinRange: `At today's rates this loop earns while leverage stays under ${rc.breakEven?.toFixed(2)}x, and costs more than it earns above that. Rates move with utilisation, so the crossing point moves with them.`,
    earnsEverywhere: `At today's rates ${collateral.symbol} earnings outrun the ${debt.symbol} borrow cost, so every level of leverage improves the return. That holds only while rates stay where they are.`,
    flat: `At today's rates earnings match the borrow cost, so the return holds steady at every leverage. Both move with utilisation.`,
    crossesBeyondCeiling: `At today's rates this loop earns at every reachable leverage, and the crossing sits past the ${loop.leverage.toFixed(1)}x ceiling. Rates move with utilisation, so that margin moves with them.`,
    costsEverywhere: `At today's rates the ${debt.symbol} borrow cost outruns ${collateral.symbol} earnings, so each turn of leverage adds to the cost of holding. Rates move with utilisation, so this picture moves with them.`,
  }[rc.kind] + (hasRewards ? " Earnings count interest, HORIZON rewards and native collateral yield at today's rates, quoted on a $10,000 open." : '')

  return (
    <Panel style={{ padding: 28 }}>
      <SectionLabel>Return against leverage</SectionLabel>
      <div style={{ margin: '8px 0 20px', color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6, maxWidth: 640 }}>
        {story}
      </div>
      <CurveChart
        lines={[{ points: curve.map((p) => ({ x: p.leverage, y: p.netApyPct })), color: T.positive, label: 'Net APY' }]}
        markers={markers}
        formatX={(v) => `${v.toFixed(1)}x`}
        formatY={(v) => `${v.toFixed(1)}%`}
      />
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// My Position
// ─────────────────────────────────────────────────────────────────

function PositionTab({ loop, user }) {
  const { collateral, debt } = loop
  // Called before the early returns below - it is a hook, and the two guards are exactly the cases
  // where it returns `known: false` anyway.
  const position = useLoopPosition(loop)

  const exposureUsd = collateral.user.supplyBalance * collateral.priceUsd
  const debtUsd = debt.user.debt * debt.priceUsd
  const equityUsd = exposureUsd - debtUsd
  const lev = currentLeverage(exposureUsd, debtUsd)
  const hasPosition = collateral.user.supplyBalance > 0 && debt.user.debt > 0

  if (!user) {
    return <EmptyState title="Connect a wallet" body="Your exposure, debt and liquidation price appear here once a wallet is connected." />
  }
  if (!hasPosition) {
    return <EmptyState title="No active position" body="Deposit from the panel beside this to open one." />
  }

  const liqPrice = liquidationPrice(collateral.user.supplyBalance, debt.user.debt, collateral.liquidationThresholdBps)
  const spotRatio = debt.priceUsd > 0 ? collateral.priceUsd / debt.priceUsd : 0
  const room = liqPrice != null && spotRatio > 0 ? Math.max(0, (1 - liqPrice / spotRatio) * 100) : null

  return (
    <>
      <div style={cardsRow}>
        <Stat boxed label="Exposure" value={fmtUsd(exposureUsd)} sub={`${fmt(collateral.user.supplyBalance, 4)} ${collateral.symbol}`} />
        <Stat boxed label="Debt" value={fmtUsd(debtUsd)} sub={`${fmt(debt.user.debt, 4)} ${debt.symbol}`} />
        <Stat boxed label="Your equity" value={fmtUsd(equityUsd)} sub="exposure less debt" />
      </div>

      <Panel style={{ padding: 28 }}>
        <SectionLabel>Position detail</SectionLabel>
        <div style={{ marginTop: 18 }}>
          <StatGrid items={[
            { label: 'Current leverage', value: `${lev.toFixed(2)}x` },
            { label: 'Max leverage', value: `${loop.leverage.toFixed(1)}x` },
            {
              label: 'Liquidation price',
              value: liqPrice != null ? `${fmt(liqPrice, 4)} ${debt.symbol}` : '—',
              help: `Priced in ${debt.symbol} rather than dollars: these two assets move together, so the ratio between them is what threatens this position.`,
            },
            { label: `Current ${collateral.symbol}/${debt.symbol}`, value: spotRatio > 0 ? fmt(spotRatio, 4) : '—' },
            { label: 'Room before liquidation', value: room != null ? `${room.toFixed(1)}%` : '—', color: room != null && room < 10 ? T.warning : T.text },
            { label: 'Net APY at this leverage', value: fmtBps(loop.netApyBps) },
          ]} />
        </div>
      </Panel>

      <PnlPanel loop={loop} position={position} />

      <Panel style={{ padding: 28 }}>
        <SectionLabel>Reducing risk</SectionLabel>
        <div style={{ marginTop: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.7, maxWidth: 720 }}>
          Closing unwinds the whole position in one transaction. To lower leverage while staying
          open, repay {debt.symbol} directly on{' '}
          <Link to={`/markets/${debt.asset}`} style={{ color: T.accent, textDecoration: 'none' }}>
            the {debt.symbol} market
          </Link>
          {' '}and every unit repaid raises your liquidation room.
        </div>
      </Panel>
    </>
  )
}

/// Profit and loss against the cost basis reconstructed from the router's own events.
///
/// **Denominated in the collateral, not dollars.** A dollar figure needs the collateral's price at
/// every deposit, which this app cannot reconstruct without an indexer — the same limitation
/// `useHistoricalStats` records for its series. In collateral units it needs only today's ratio, so
/// the number is real rather than approximated, and "you put in 5 WETH and your stake is worth 5.4"
/// is the figure a levered holder is actually watching.
///
/// Renders nothing at all when the basis is unknown. A P&L panel showing dashes invites the reader to
/// assume the position is flat, which is a worse outcome than an absent panel.
function PnlPanel({ loop, position }) {
  if (position.loading) return <Skeleton height={140} />
  const pnl = computeLoopPnl({ position, loop })
  if (!pnl) return null

  const { collateral, debt } = loop
  const up = pnl.pnl >= 0
  const tone = up ? T.positive : T.negative

  return (
    <Panel style={{ padding: 28 }}>
      <SectionLabel>Profit and loss</SectionLabel>
      <div style={{ marginTop: 18 }}>
        <StatGrid items={[
          {
            label: 'Deposited',
            value: `${fmt(position.principal, 4)} ${collateral.symbol}`,
            help: position.opens > 1
              ? `Summed across ${position.opens} opens since your last close. Closing unwinds fully, so the basis resets there rather than counting a position you already exited.`
              : 'Your own capital, excluding everything the loop borrowed on top of it.',
          },
          { label: 'Worth now', value: `${fmt(pnl.equity, 4)} ${collateral.symbol}`, help: 'Collateral held less debt owed, converted at today’s ratio.' },
          {
            label: 'Profit / loss',
            value: `${up ? '+' : ''}${fmt(pnl.pnl, 4)} ${collateral.symbol}`,
            color: tone,
          },
          {
            label: 'Return on deposit',
            value: pnl.pnlPct != null ? `${up ? '+' : ''}${pnl.pnlPct.toFixed(2)}%` : '—',
            color: tone,
          },
          {
            label: 'Entry price',
            value: position.entryPrice != null ? `${fmt(position.entryPrice, 4)} ${debt.symbol}` : '—',
            help: position.entryPrice != null
              ? 'The rate your opens actually executed at, swap fee and slippage included: what the position cost, rather than what the oracle said the price was.'
              : `Nothing was swapped on the way in, so there is no executed ${collateral.symbol}/${debt.symbol} rate to report.`,
          },
          {
            label: 'Move since entry',
            value: pnl.priceChangePct != null ? `${pnl.priceChangePct >= 0 ? '+' : ''}${pnl.priceChangePct.toFixed(2)}%` : '—',
            help: 'How far the pair has travelled since you opened, which is what drives the figures above.',
          },
        ]} />
      </div>
      <div style={{ marginTop: 16, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.small, lineHeight: 1.7, maxWidth: 720 }}>
        Measured in {collateral.symbol} rather than dollars. Borrow interest and any supply yield are
        already inside these numbers: they move your collateral and debt balances, so they show up in
        what the position is worth.
      </div>
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Info & FAQs
// ─────────────────────────────────────────────────────────────────

function InfoTab({ loop }) {
  const { collateral, debt } = loop
  return (
    <div style={infoLayout}>
      <Panel style={{ padding: 28 }}>
        <SectionLabel>Frequently asked</SectionLabel>
        <div style={{ marginTop: 10 }}>
          <FaqAccordion items={multiplyFaq(loop)} />
        </div>
      </Panel>

      <Panel style={{ padding: 28, alignSelf: 'flex-start' }}>
        <SectionLabel>Loop details</SectionLabel>
        <div style={{ marginTop: 18 }}>
          <StatGrid columns={1} items={[
            { label: 'Strategy', value: loop.kind, mono: false },
            { label: 'Collateral', value: <AddressValue address={collateral.asset} /> },
            { label: 'Debt', value: <AddressValue address={debt.asset} /> },
            { label: 'Max LTV', value: fmtBps(collateral.collateralFactorBps) },
            { label: 'Liquidation threshold', value: fmtBps(collateral.liquidationThresholdBps) },
            { label: 'Liquidation bonus', value: fmtBps(collateral.liquidationBonusBps) },
            { label: 'Available liquidity', value: fmtUsd(loop.liquidityUsd) },
          ]} />
        </div>
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Link to={`/markets/${collateral.asset}`} style={linkStyle}>The {collateral.symbol} market →</Link>
          <Link to={`/markets/${debt.asset}`} style={linkStyle}>The {debt.symbol} market →</Link>
        </div>
      </Panel>
    </div>
  )
}

function AddressValue({ address }) {
  if (!address) return <span style={{ color: T.textDim }}>—</span>
  return (
    <span className="mono" style={{ color: T.textMuted, fontSize: T.fs.caption }} title={address}>
      {`${address.slice(0, 6)}…${address.slice(-4)}`}
    </span>
  )
}

function EmptyState({ title, body }) {
  return (
    <Panel elevated style={{ padding: 48, textAlign: 'center' }}>
      <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>{title}</div>
      <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>{body}</div>
    </Panel>
  )
}

// One tone map for every carry figure - see carryPresentation for why 'neutral' exists.
const TONE = { positive: T.positive, negative: T.negative, neutral: T.textMuted }

const layout = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 400px)',
  gap: 24,
  alignItems: 'start',
}
const layoutNarrow = {
  display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 24, alignItems: 'start',
}
const infoLayout = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, alignItems: 'start',
}
const cardsRow = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12,
}
const panelHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
}
const backLinkStyle = { color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, textDecoration: 'none' }
const linkStyle = { color: T.accent, fontFamily: T.fontM, fontSize: T.fs.caption, textDecoration: 'none' }
